import { createApi } from '@reduxjs/toolkit/query/react';

import {
  FamilyAccountInfo,
  LeaveFamilyResponse,
  RevokedInviteResponse,
  ResendFamilyInviteResponse,
  RemoveFamilyMemberResponse,
  SendFamilyInvitePayload,
  SendFamilyInviteResponse,
} from './family.types';
import { baseQueryWithAuth } from '../baseQuery';

export const familyApi = createApi({
  reducerPath: 'familyApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['FamilyAccount'],
  endpoints: (build) => ({
    getFamilyAccount: build.query<FamilyAccountInfo, undefined>({
      query: () => 'family-accounts/my-account',
      providesTags: [{ type: 'FamilyAccount' }],
    }),
    sendFamilyInvite: build.mutation<SendFamilyInviteResponse, SendFamilyInvitePayload[]>({
      query: (invites) => ({
        url: 'family-accounts/invites',
        method: 'POST',
        body: invites,
      }),
      invalidatesTags: [{ type: 'FamilyAccount' }],
    }),
    resendFamilyInvite: build.mutation<ResendFamilyInviteResponse, number>({
      query: (inviteId) => ({
        url: `family-accounts/invites/${inviteId}/resend`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'FamilyAccount' }],
    }),
    removeFamilyMember: build.mutation<RemoveFamilyMemberResponse, number>({
      query: (memberId) => ({
        url: `family-accounts/members/${memberId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'FamilyAccount' }],
    }),
    revokeFamilyInvite: build.mutation<RevokedInviteResponse, number>({
      query: (inviteId) => ({
        url: `family-accounts/invites/${inviteId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'FamilyAccount' }],
    }),
    leaveFamily: build.mutation<LeaveFamilyResponse, undefined>({
      query: () => ({
        url: 'family-accounts/members/me',
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'FamilyAccount' }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Import userApi dynamically to avoid circular dependency
          const { userApi } = await import('../user/user.api');

          dispatch(userApi.util.invalidateTags(['UserPermissions']));
        } catch (error) {
          console.error('Leave family failed:', error);
        }
      },
    }),
  }),
});

export const {
  useGetFamilyAccountQuery,
  useSendFamilyInviteMutation,
  useResendFamilyInviteMutation,
  useRemoveFamilyMemberMutation,
  useRevokeFamilyInviteMutation,
  useLeaveFamilyMutation,
  util: { resetApiState },
} = familyApi;
